import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { FOCUS_MODE_SHADOWS, getFocusModeValue } from '@src/constants/design-tokens';
import { COLORS } from '@src/constants/design-tokens/colors';
import { selectFocusMode } from '@src/store/slices/focus_mode';
import { memo, type ReactElement } from 'react';
import { useSelector } from 'react-redux';

type PdfErrorWarningProps = {
  error: { type: string; message: string };
  onRetry: () => void;
};

/**
 * メモ化されたPDFエラー警告コンポーネント
 *
 * PDFのレンダリングエラーを視覚的に表示し、再試行やサンプルPDF表示の機能を提供します。
 *
 * @param {PdfErrorWarningProps} props - コンポーネントのプロパティ
 * @param {object} props.error - エラー情報
 * @param {string} props.error.type - エラーの種類
 * @param {string} props.error.message - エラーメッセージ
 * @param {() => void} props.onRetry - 再試行時のコールバック
 * @returns {ReactElement} エラー警告コンポーネント
 */
export const PdfErrorWarning = memo(({ error, onRetry }: PdfErrorWarningProps): ReactElement => {
  const isFocusMode = useSelector(selectFocusMode);

  // エラーの種類に応じたアイコンと色を決定
  const getErrorIcon = () => {
    switch (error.type) {
      case 'corrupted_pdf':
      case 'invalid_parameters':
        return '⚠️';
      case 'encrypted_pdf':
        return '🔒';
      case 'unsupported_pdf':
        return '📄';
      case 'network_error':
      case 'timeout_error':
        return '🌐';
      case 'cors_error':
        return '🚫';
      default:
        return '❌';
    }
  };

  const getErrorColor = () => {
    switch (error.type) {
      case 'corrupted_pdf':
      case 'invalid_parameters':
        return COLORS.semantic.warning.dark; // オレンジ
      case 'encrypted_pdf':
        return COLORS.primary.main; // 青
      case 'unsupported_pdf':
        return COLORS.semantic.success.dark; // 緑
      case 'network_error':
      case 'timeout_error':
        return COLORS.semantic.error.dark; // 赤
      case 'cors_error':
        return COLORS.accent.purple; // 紫
      default:
        return COLORS.semantic.error.dark; // 赤
    }
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: isFocusMode
          ? COLORS.focusMode.accordion.summary.background
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(12px)',
        borderRadius: 3,
        padding: 4,
        maxWidth: '90%',
        width: '400px',
        textAlign: 'center',
        boxShadow: getFocusModeValue(FOCUS_MODE_SHADOWS, 'primary', isFocusMode),
        border: `2px solid ${getErrorColor()}`,
        zIndex: 1000,
      }}
    >
      <Typography
        variant="h1"
        sx={{
          fontSize: '3rem',
          marginBottom: 2,
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
        }}
      >
        {getErrorIcon()}
      </Typography>

      <Typography
        variant="h6"
        sx={{
          marginBottom: 2,
          color: isFocusMode ? COLORS.darkMode.text.primary : COLORS.text.primary,
          fontWeight: 'bold',
        }}
      >
        PDFファイルの警告
      </Typography>

      <Typography
        variant="body1"
        sx={{
          marginBottom: 3,
          color: isFocusMode ? COLORS.darkMode.text.secondary : COLORS.text.secondary,
          lineHeight: 1.6,
        }}
      >
        {error.message}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button
          variant="contained"
          onClick={onRetry}
          sx={{
            backgroundColor: getErrorColor(),
            color: COLORS.primary.contrastText,
            '&:hover': {
              backgroundColor: getErrorColor(),
              opacity: 0.8,
            },
          }}
        >
          再試行
        </Button>

        <Button
          variant="outlined"
          onClick={() => window.open('/resume_example.pdf', '_blank')}
          sx={{
            borderColor: getErrorColor(),
            color: getErrorColor(),
            '&:hover': {
              borderColor: getErrorColor(),
              backgroundColor: `${getErrorColor()}10`,
            },
          }}
        >
          サンプルPDFを開く
        </Button>
      </Box>

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          marginTop: 2,
          color: isFocusMode ? COLORS.focusMode.text.tertiary : COLORS.text.hint,
          fontSize: '0.75rem',
        }}
      >
        エラーコード: {error.type}
      </Typography>
    </Box>
  );
});

PdfErrorWarning.displayName = 'PdfErrorWarning';
